"use client";

import { NextLink } from "@/components/ui/next-link";
import { Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface BankTransferInfoProps {
  fee: number;
  accountHolder: string;
  iban: string;
  bankName: string;
  reference: string;
  eventId: number;
}

function CopyableField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const copy = () => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} kopiert.`);
  };

  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-1.5">
      <span className="text-xs text-muted-foreground w-28 shrink-0">{label}</span>
      <span className={`text-sm flex-1 min-w-0 break-all ${mono ? "font-mono" : ""}`}>{value}</span>
      <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={copy}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function BankTransferInfo({ fee, accountHolder, iban, bankName, reference, eventId }: BankTransferInfoProps) {
  const ibanClean = iban.replace(/\s+/g, "");
  // EPC QR Code ("GiroCode") — SEPA credit transfer, scannable by banking apps.
  // BIC is left empty: optional for version "002" as long as the IBAN is within SEPA.
  const epcPayload = ["BCD", "002", "1", "SCT", "", accountHolder, ibanClean, `EUR${fee.toFixed(2)}`, "", "", reference].join("\n");

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div>
        <p className="text-sm font-semibold">Überweisung</p>
        <p className="text-xs text-muted-foreground mt-0.5">Bitte den Betrag manuell überweisen, oder den QR-Code mit deiner Banking-App scannen.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        {ibanClean && (
          <div className="self-center sm:self-start shrink-0 bg-white p-2 rounded-md w-fit">
            <QRCodeSVG value={epcPayload} size={128} level="M" marginSize={1} />
          </div>
        )}

        <div className="flex-1 min-w-0 space-y-3">
          <p className="text-xl font-semibold tabular-nums">{fee.toFixed(2).replace(".", ",")} €</p>

          <div className="space-y-1.5">
            <CopyableField label="Inhaber" value={accountHolder} />
            <CopyableField label="IBAN" value={iban} mono />
            <CopyableField label="Bank" value={bankName} />
            <CopyableField label="Verwendungszweck" value={reference} mono />
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">Wenn ihr für mehrere überweisen wollt, gebt bitte die weiteren IDs und Namen durch Komma getrennt an, z.B.: {reference}, &lt;id&gt; &lt;nick&gt;, &lt;id&gt; &lt;nick&gt;, ...</p>

      <p className="text-xs text-muted-foreground">
        Während wir deine Überweisung bearbeiten, merke dir im{" "}
        <NextLink href={`/home/events/${eventId}/sitzplan`} className="underline hover:text-foreground">
          Sitzplan
        </NextLink>{" "}
        schon mal Plätze vor. Achtung: Diese Plätze können von bezahlten Gästen reserviert werden.
      </p>
    </div>
  );
}
